import {
  ControlCategories,
  ControlCategory,
  ControlCode,
  ControlCodes,
  Feature,
  ValidControlCategory,
  ValidControlCode,
} from "@theprelude/sdk";
import { assertNever } from "./assert-never";

export const flaggedControls: Array<{ control: ControlCode; flag: Feature }> = [
  // detect
  { control: ControlCodes.GITHUB, flag: "detect" },
  { control: ControlCodes.S3, flag: "detect" },
  { control: ControlCodes.SPLUNK, flag: "detect" },
  { control: ControlCodes.VECTR, flag: "detect" },
  // policy_evaluator
  { control: ControlCodes.AWS_SSM, flag: "policy_evaluator" },
  { control: ControlCodes.CROWDSTRIKE_IDENTITY, flag: "policy_evaluator" },
  { control: ControlCodes.DEFENDER_DISCOVERY, flag: "policy_evaluator" },
  { control: ControlCodes.EC2, flag: "policy_evaluator" },
  { control: ControlCodes.ENTRA, flag: "policy_evaluator" },
  { control: ControlCodes.INTUNE, flag: "policy_evaluator" },
  { control: ControlCodes.GMAIL, flag: "policy_evaluator" },
  { control: ControlCodes.GOOGLE_IDENTITY, flag: "policy_evaluator" },
  { control: ControlCodes.JAMF, flag: "policy_evaluator" },
  { control: ControlCodes.M365, flag: "policy_evaluator" },
  { control: ControlCodes.OKTA, flag: "policy_evaluator" },
  { control: ControlCodes.SERVICENOW, flag: "policy_evaluator" },
  { control: ControlCodes.TENABLE, flag: "policy_evaluator" },
] as const;

export function getRFMDescription(control: ControlCode): string {
  switch (control) {
    case ControlCodes.CROWDSTRIKE: {
      return "Reduced Functionality Mode";
    }
    case ControlCodes.DEFENDER:
    case ControlCodes.DEFENDER_DISCOVERY: {
      return "Passive Mode";
    }
    case ControlCodes.SENTINELONE: {
      return "Operational State";
    }
    default: {
      return "Reduced Functionality Mode";
    }
  }
}

export const getControlCategory = (control: ControlCode): ControlCategory => {
  if (!isValidControl(control)) {
    return ControlCategories.INVALID;
  }
  return getValidControlCategory(control);
};

export const getValidControlCategory = (
  control: ValidControlCode,
): ValidControlCategory => {
  if (isDefensiveControl(control)) {
    return ControlCategories.XDR;
  }
  if (isEndpointManagementControl(control)) {
    return ControlCategories.ASSET_MANAGER;
  }
  if (isVulnerabilityManagementControl(control)) {
    return ControlCategories.VULN_MANAGER;
  }
  if (isIdentityProviderControl(control)) {
    return ControlCategories.IDENTITY;
  }
  if (isEmailControl(control)) {
    return ControlCategories.EMAIL;
  }
  if (isDiscoveredDevicesControl(control)) {
    return ControlCategories.DISCOVERED_DEVICES;
  }
  if (isSIEMControl(control)) {
    return ControlCategories.SIEM;
  }
  if (isPrivateRepoControl(control)) {
    return ControlCategories.PRIVATE_REPO;
  }
  if (isVulnerabilityManagementControl(control)) {
    return ControlCategories.VULN_MANAGER;
  }
  return ControlCategories.XDR;
};

export const getControlCategoryName = (category?: ControlCategory) => {
  if (category === undefined || category === ControlCategories.INVALID) {
    return "";
  }

  switch (category) {
    case ControlCategories.ASSET_MANAGER: {
      return "Endpoint Management";
    }
    case ControlCategories.CLOUD: {
      return "Cloud";
    }
    case ControlCategories.DISCOVERED_DEVICES: {
      return "Discovered Devices";
    }
    case ControlCategories.EMAIL: {
      return "Email";
    }
    case ControlCategories.IDENTITY: {
      return "Identity Provider";
    }
    case ControlCategories.NETWORK: {
      return "Network";
    }
    case ControlCategories.PRIVATE_REPO: {
      return "Private Repository";
    }
    case ControlCategories.SIEM: {
      return "SIEM";
    }
    case ControlCategories.XDR: {
      return "EDR";
    }
    case ControlCategories.VULN_MANAGER: {
      return "Vulnerability Management";
    }
    default: {
      return assertNever(category);
    }
  }
};

export const getControlName = (control?: ControlCode) => {
  if (control === undefined) {
    return "";
  }
  switch (control) {
    case ControlCodes.NONE: {
      return "No EDR";
    }
    case ControlCodes.AWS_SSM: {
      return "Amazon SSM";
    }
    case ControlCodes.AZURE_VM: {
      return "Azure VM";
    }
    case ControlCodes.CROWDSTRIKE: {
      return "CrowdStrike";
    }
    case ControlCodes.CROWDSTRIKE_IDENTITY: {
      return "CrowdStrike Identity Protection";
    }
    case ControlCodes.DEFENDER:
    case ControlCodes.DEFENDER_DISCOVERY: {
      return "Microsoft Defender";
    }
    case ControlCodes.EC2: {
      return "Amazon EC2";
    }
    case ControlCodes.ENTRA: {
      return "Microsoft Entra ID";
    }
    case ControlCodes.GITHUB: {
      return "GitHub";
    }
    case ControlCodes.GMAIL: {
      return "Gmail";
    }
    case ControlCodes.GOOGLE_IDENTITY: {
      return "Google Cloud Identity Platform";
    }
    case ControlCodes.INTUNE: {
      return "Microsoft Intune";
    }
    case ControlCodes.JAMF: {
      return "Jamf";
    }
    case ControlCodes.M365: {
      return "Microsoft 365";
    }
    case ControlCodes.OKTA: {
      return "Okta";
    }
    case ControlCodes.SERVICENOW: {
      return "ServiceNow";
    }
    case ControlCodes.SPLUNK: {
      return "Splunk";
    }
    case ControlCodes.S3: {
      return "Amazon S3";
    }
    case ControlCodes.SENTINELONE: {
      return "SentinelOne";
    }
    case ControlCodes.VECTR: {
      return "VECTR";
    }
    case ControlCodes.TENABLE:
    case ControlCodes.TENABLE_DISCOVERY: {
      return "Tenable";
    }
    case ControlCodes.INVALID: {
      return "";
    }
    default: {
      return assertNever(control);
    }
  }
};

export const controlOptions: { id: ValidControlCode; name: string }[] =
  Object.values(ControlCodes)
    .filter(isValidControl)
    .map((value) => ({
      id: value,
      name: getControlName(value),
    }));

export function isValidControl(
  control: ControlCode,
): control is ValidControlCode {
  return control !== ControlCodes.NONE && control !== ControlCodes.INVALID;
}

export function isDefensiveControl(
  control: ControlCode,
): control is
  | typeof ControlCodes.CROWDSTRIKE
  | typeof ControlCodes.DEFENDER
  | typeof ControlCodes.SENTINELONE {
  return (
    control === ControlCodes.CROWDSTRIKE ||
    control === ControlCodes.DEFENDER ||
    control === ControlCodes.SENTINELONE
  );
}

export function isDataControl(
  control: ControlCode,
): control is
  | typeof ControlCodes.SPLUNK
  | typeof ControlCodes.S3
  | typeof ControlCodes.VECTR {
  return (
    control === ControlCodes.SPLUNK ||
    control === ControlCodes.S3 ||
    control === ControlCodes.VECTR
  );
}

export const isSIEMControl = isDataControl;

export function isEndpointManagementControl(
  control: ControlCode,
): control is
  | typeof ControlCodes.AWS_SSM
  | typeof ControlCodes.INTUNE
  | typeof ControlCodes.JAMF {
  return (
    control === ControlCodes.AWS_SSM ||
    control === ControlCodes.INTUNE ||
    control === ControlCodes.JAMF
  );
}

export function isVulnerabilityManagementControl(
  control: ControlCode,
): control is typeof ControlCodes.TENABLE {
  return control === ControlCodes.TENABLE;
}

export function isDiscoveredDevicesControl(
  control: ControlCode,
): control is
  | typeof ControlCodes.DEFENDER_DISCOVERY
  | typeof ControlCodes.EC2
  | typeof ControlCodes.SERVICENOW
  | typeof ControlCodes.AZURE_VM {
  return (
    control == ControlCodes.DEFENDER_DISCOVERY ||
    control == ControlCodes.EC2 ||
    control === ControlCodes.SERVICENOW ||
    control === ControlCodes.AZURE_VM
  );
}

export function isEmailControl(
  control: ControlCode,
): control is typeof ControlCodes.GMAIL | typeof ControlCodes.M365 {
  return control === ControlCodes.GMAIL || control === ControlCodes.M365;
}

export function isIdentityProviderControl(
  control: ControlCode,
): control is
  | typeof ControlCodes.CROWDSTRIKE_IDENTITY
  | typeof ControlCodes.ENTRA
  | typeof ControlCodes.GOOGLE_IDENTITY
  | typeof ControlCodes.OKTA {
  return (
    control === ControlCodes.CROWDSTRIKE_IDENTITY ||
    control === ControlCodes.ENTRA ||
    control === ControlCodes.GOOGLE_IDENTITY ||
    control === ControlCodes.OKTA
  );
}

export function isPrivateRepoControl(
  control: ControlCode,
): control is typeof ControlCodes.GITHUB {
  return control === ControlCodes.GITHUB;
}

export const getCategoryControls = (
  category: ControlCategory,
): ValidControlCode[] => {
  const filterBy = (filter: (c: ValidControlCode) => boolean) =>
    Object.values(ControlCodes)
      .filter(
        (code): code is ValidControlCode =>
          isValidControl(code) && filter(code),
      )
      .sort((a, b) => getControlName(a).localeCompare(getControlName(b)));

  switch (category) {
    case ControlCategories.ASSET_MANAGER: {
      return filterBy(isEndpointManagementControl);
    }
    case ControlCategories.DISCOVERED_DEVICES: {
      return filterBy(isDiscoveredDevicesControl);
    }
    case ControlCategories.EMAIL: {
      return filterBy(isEmailControl);
    }
    case ControlCategories.IDENTITY: {
      return filterBy(
        (c) =>
          isIdentityProviderControl(c) &&
          c !== ControlCodes.CROWDSTRIKE_IDENTITY,
      );
    }
    case ControlCategories.XDR: {
      return filterBy(isDefensiveControl);
    }
    case ControlCategories.VULN_MANAGER: {
      return filterBy(isVulnerabilityManagementControl);
    }
    case ControlCategories.CLOUD:
    case ControlCategories.INVALID:
    case ControlCategories.NETWORK:
    case ControlCategories.PRIVATE_REPO:
    case ControlCategories.SIEM: {
      return [];
    }
    default: {
      return assertNever(category);
    }
  }
};
